/**
 * Modal de criação/edição de tenant (superadmin).
 *
 * Modos:
 *  - Criação (sem tenantId)  → cria tenant + branding inicial
 *  - Edição  (com tenantId)  → atualiza tenant + branding em chamadas separadas
 *
 * Tabs:
 *  - Geral    → slug, plan, active, max_external_dispatches
 *  - Branding → app_name, candidate_name, party, slogan, cores, logo_url
 *  - Membros  → lista de membros + convites pendentes + formulário de convite (apenas edição)
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  X, Loader2, Building2, Palette, Save, AlertTriangle, Sparkles,
  Users, Plus, Trash2, Mail, ToggleLeft, ToggleRight, Clock, CheckCircle2,
  MessageCircle, Phone, Wifi, WifiOff, ShieldCheck, Search, CheckCircle, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import {
  useCreateTenant, useUpdateTenant, useUpdateTenantBranding,
  useTenantDetail,
} from '../hooks/useTenantsAdmin';
import {
  useTenantMembers, useTenantInvitations,
  useUpdateMemberRole, useToggleMemberActive, useRemoveMember,
  useCreateInvitation, useRevokeInvitation,
  ROLE_LABELS, ROLE_COLORS,
  type TenantRole,
} from '../hooks/useTenantMembers';
import {
  useTenantWhatsApp, useUpsertTenantWhatsApp, useUpdateWhatsAppStatus, useDeleteTenantWhatsApp,
  useTenantPlanLimits, useUpdateTenantPlanLimits,
  useTenantMessagingSettings, useUpsertMessagingSettings,
  useWhatsAppTemplates, useUpdateWhatsAppTemplate,
  WA_STATUS_LABELS, WA_STATUS_COLORS, META_TIER_LABELS,
  type WhatsAppStatus, type WhatsAppTemplate, type HeaderType, type TemplateUseCase,
} from '../hooks/useTenantWhatsApp';

// ── Schema ───────────────────────────────────────────────────────────────────

// Fusos brasileiros disponíveis para seleção
const BR_TIMEZONES: { value: string; label: string }[] = [
  { value: 'America/Sao_Paulo',    label: 'Brasília / São Paulo (UTC-3)' },
  { value: 'America/Fortaleza',    label: 'Fortaleza / Recife / Salvador (UTC-3)' },
  { value: 'America/Belem',        label: 'Belém / Macapá (UTC-3)' },
  { value: 'America/Manaus',       label: 'Manaus / Boa Vista (UTC-4)' },
  { value: 'America/Porto_Velho',  label: 'Porto Velho (UTC-4)' },
  { value: 'America/Cuiaba',       label: 'Cuiabá (UTC-4 / AMST)' },
  { value: 'America/Campo_Grande', label: 'Campo Grande (UTC-4 / AMST)' },
  { value: 'America/Rio_Branco',   label: 'Rio Branco / Acre (UTC-5)' },
  { value: 'America/Noronha',      label: 'Fernando de Noronha (UTC-2)' },
];

const schema = z.object({
  // Geral
  slug: z.string().min(2, 'Mínimo 2 caracteres').max(60),
  plan: z.enum(['essencial', 'pro', 'enterprise']),
  active: z.boolean(),
  max_external_dispatches: z.number().int().min(0).max(10000),
  timezone: z.string().min(1, 'Selecione o fuso horário'),

  // Branding
  app_name:       z.string().min(1, 'Obrigatório').max(80),
  candidate_name: z.string().max(120).optional().or(z.literal('')),
  party:          z.string().max(60).optional().or(z.literal('')),
  slogan:         z.string().max(160).optional().or(z.literal('')),
  primary_color:  z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Hex no formato #RRGGBB'),
  accent_color:   z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Hex no formato #RRGGBB'),
  logo_url:       z.string().url('URL inválida').optional().or(z.literal('')),
  uf:             z.string().length(2, 'Sigla com 2 letras').toUpperCase().optional().or(z.literal('')),
  municipio:      z.string().max(100).optional().or(z.literal('')),
});

type FormValues = z.infer<typeof schema>;

const DEFAULTS: FormValues = {
  slug:                    '',
  plan:                    'essencial',
  active:                  true,
  max_external_dispatches: 50,
  timezone:                'America/Sao_Paulo',
  app_name:                'Lumenise Base',
  candidate_name:          '',
  party:                   '',
  slogan:                  '',
  primary_color:           '#0A2342',
  accent_color:            '#00C2FF',
  logo_url:                '',
  uf:                      '',
  municipio:               '',
};

// ── Props ────────────────────────────────────────────────────────────────────

interface TenantFormModalProps {
  open:      boolean;
  tenantId?: string | null;   // null/undefined = modo criação
  onClose:   () => void;
}

export function TenantFormModal({ open, tenantId, onClose }: TenantFormModalProps) {
  const [tab, setTab] = useState<'geral' | 'branding' | 'membros' | 'whatsapp'>('geral');

  const isEdit = !!tenantId;
  const { data: detail, isLoading: loadingDetail } = useTenantDetail(open && isEdit ? tenantId : null);

  const create         = useCreateTenant();
  const updateTenant   = useUpdateTenant();
  const updateBranding = useUpdateTenantBranding();

  const {
    register, handleSubmit, reset, setValue, watch, formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULTS,
  });

  // Preenche o form quando carrega o detalhe ou abre em modo criação
  useEffect(() => {
    if (!open) return;
    if (!isEdit) {
      reset(DEFAULTS);
      setTab('geral');
      return;
    }
    if (detail) {
      const b = detail.tenant_branding;
      reset({
        slug:                    detail.slug,
        plan:                    detail.plan,
        active:                  detail.active,
        max_external_dispatches: detail.max_external_dispatches ?? 50,
        timezone:                detail.timezone ?? 'America/Sao_Paulo',
        app_name:                b?.app_name ?? 'Lumenise Base',
        candidate_name:          b?.candidate_name ?? '',
        party:                   b?.party ?? '',
        slogan:                  b?.slogan ?? '',
        primary_color:           b?.primary_color ?? '#0A2342',
        accent_color:            b?.accent_color  ?? '#00C2FF',
        logo_url:                b?.logo_url ?? '',
        uf:                      b?.uf ?? '',
        municipio:               b?.municipio ?? '',
      });
    }
  }, [open, isEdit, detail, reset]);

  if (!open) return null;

  const submitting = create.isPending || updateTenant.isPending || updateBranding.isPending;
  const accentPreview = watch('accent_color');
  const primaryPreview = watch('primary_color');
  const appNamePreview = watch('app_name');

  // ── Submit ─────────────────────────────────────────────────────────────────
  const onSubmit = handleSubmit(async (values) => {
    try {
      if (isEdit && tenantId) {
        // 1) Atualiza tenant
        await updateTenant.mutateAsync({
          id:                      tenantId,
          slug:                    values.slug,
          plan:                    values.plan,
          active:                  values.active,
          max_external_dispatches: values.max_external_dispatches,
          timezone:                values.timezone,
        });
        // 2) Atualiza branding
        await updateBranding.mutateAsync({
          tenant_id:      tenantId,
          app_name:       values.app_name,
          candidate_name: nullify(values.candidate_name),
          party:          nullify(values.party),
          slogan:         nullify(values.slogan),
          primary_color:  values.primary_color,
          accent_color:   values.accent_color,
          logo_url:       nullify(values.logo_url),
          uf:             nullify(values.uf?.toUpperCase()),
          municipio:      nullify(values.municipio),
        });
        toast.success('Tenant atualizado');
      } else {
        await create.mutateAsync({
          slug:                    values.slug,
          plan:                    values.plan,
          active:                  values.active,
          max_external_dispatches: values.max_external_dispatches,
          app_name:                values.app_name,
          candidate_name:          nullify(values.candidate_name),
          party:                   nullify(values.party),
          slogan:                  nullify(values.slogan),
          primary_color:           values.primary_color,
          accent_color:            values.accent_color,
          logo_url:                nullify(values.logo_url),
        });
        toast.success('Tenant criado com sucesso');
      }
      onClose();
    } catch (e) {
      toast.error('Falha ao salvar', { description: e instanceof Error ? e.message : 'Erro desconhecido' });
    }
  });

  // ── Render ─────────────────────────────────────────────────────────────────
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-2xl glass-strong border border-hairline shadow-deep flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 hairline-b shrink-0">
          <div className="h-9 w-9 rounded-xl bg-accent/15 border border-accent/25 flex items-center justify-center">
            <Building2 className="h-4 w-4 text-accent" />
          </div>
          <div className="flex-grow">
            <h2 className="font-display text-lg font-bold text-ink">
              {isEdit ? 'Editar tenant' : 'Novo tenant'}
            </h2>
            <p className="text-[11px] text-ink-muted">
              {isEdit ? `ID: ${tenantId?.slice(0, 8)}…` : 'Provisiona uma nova campanha na plataforma'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-ink-subtle hover:text-ink hover:bg-white/5 transition-colors cursor-pointer"
            type="button"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 pt-4 pb-2 shrink-0">
          <TabBtn icon={Building2} label="Geral"    active={tab === 'geral'}    onClick={() => setTab('geral')}    />
          <TabBtn icon={Palette}   label="Branding" active={tab === 'branding'} onClick={() => setTab('branding')} />
          {isEdit && (
            <TabBtn icon={Users}          label="Membros"  active={tab === 'membros'}  onClick={() => setTab('membros')}  />
          )}
          {isEdit && (
            <TabBtn icon={MessageCircle} label="WhatsApp" active={tab === 'whatsapp'} onClick={() => setTab('whatsapp')} />
          )}
        </div>

        {/* Form */}
        <form onSubmit={onSubmit} className="flex-grow overflow-y-auto px-6 py-4">
          {loadingDetail && isEdit ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-accent" />
            </div>
          ) : tab === 'geral' ? (
            <div className="space-y-4" key="geral">
              <Field label="Slug" hint="Identificador único (apenas a-z, 0-9 e -)" error={errors.slug?.message}>
                <input
                  {...register('slug')}
                  type="text"
                  placeholder="campanha-2026"
                  className="w-full rounded-xl bg-midnight-2/60 border border-hairline px-3 py-2.5 text-sm text-ink placeholder-ink-faint outline-none focus:border-accent/40 transition-all"
                  autoComplete="off"
                />
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Plano" error={errors.plan?.message}>
                  <select {...register('plan')} className="w-full rounded-xl bg-midnight-2/60 border border-hairline px-3 py-2.5 text-sm text-ink placeholder-ink-faint outline-none focus:border-accent/40 transition-all">
                    <option value="essencial">Essencial</option>
                    <option value="pro">Pro</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </Field>

                <Field label="Disparos externos (máx)" hint="Limite de envios para não-contatos" error={errors.max_external_dispatches?.message}>
                  <input
                    {...register('max_external_dispatches', { valueAsNumber: true })}
                    type="number"
                    min={0}
                    max={10000}
                    className="w-full rounded-xl bg-midnight-2/60 border border-hairline px-3 py-2.5 text-sm text-ink placeholder-ink-faint outline-none focus:border-accent/40 transition-all"
                  />
                </Field>
              </div>

              <Field label="Status">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    {...register('active')}
                    type="checkbox"
                    className="h-4 w-4 rounded border-hairline bg-midnight-2 text-accent focus:ring-accent/30"
                  />
                  <span className="text-sm text-ink">Tenant ativo</span>
                  <span className="text-[11px] text-ink-subtle">(desativar bloqueia login dos membros)</span>
                </label>
              </Field>

              <Field label="Fuso horário" hint="Usado na validação da janela de envio" error={errors.timezone?.message}>
                <select
                  {...register('timezone')}
                  className="w-full rounded-xl bg-midnight-2/60 border border-hairline px-3 py-2.5 text-sm text-ink outline-none focus:border-accent/40 transition-all"
                >
                  {BR_TIMEZONES.map((tz) => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
              </Field>
            </div>
          ) : tab === 'branding' ? (
            <div className="space-y-4" key="branding">
              {/* Preview do branding */}
              <div className="rounded-xl border border-hairline p-3 flex items-center gap-3">
                <div
                  className="h-10 w-10 rounded-xl flex items-center justify-center font-black text-sm shrink-0"
                  style={{ background: primaryPreview, color: accentPreview }}
                >
                  {(appNamePreview || 'L')[0]?.toUpperCase()}
                </div>
                <div className="flex-grow min-w-0">
                  <p className="text-sm font-bold text-ink truncate">{appNamePreview || 'Sem nome'}</p>
                  <p className="text-[10px] text-ink-subtle">Preview do branding</p>
                </div>
                <Sparkles className="h-4 w-4 text-accent shrink-0" />
              </div>

              <Field label="Nome do app" hint="Aparece como título da plataforma" error={errors.app_name?.message}>
                <input {...register('app_name')} type="text" className="w-full rounded-xl bg-midnight-2/60 border border-hairline px-3 py-2.5 text-sm text-ink placeholder-ink-faint outline-none focus:border-accent/40 transition-all" placeholder="Plataforma do Candidato" />
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Nome do candidato" error={errors.candidate_name?.message}>
                  <input {...register('candidate_name')} type="text" className="w-full rounded-xl bg-midnight-2/60 border border-hairline px-3 py-2.5 text-sm text-ink placeholder-ink-faint outline-none focus:border-accent/40 transition-all" placeholder="Opcional" />
                </Field>
                <Field label="Partido" error={errors.party?.message}>
                  <input {...register('party')} type="text" className="w-full rounded-xl bg-midnight-2/60 border border-hairline px-3 py-2.5 text-sm text-ink placeholder-ink-faint outline-none focus:border-accent/40 transition-all" placeholder="Opcional" />
                </Field>
              </div>

              <Field label="Slogan" error={errors.slogan?.message}>
                <input {...register('slogan')} type="text" className="w-full rounded-xl bg-midnight-2/60 border border-hairline px-3 py-2.5 text-sm text-ink placeholder-ink-faint outline-none focus:border-accent/40 transition-all" placeholder='Opcional — ex: "Por uma cidade melhor"' />
              </Field>

              {/* Localização — define o centro padrão do mapa Field Intelligence */}
              <div className="grid grid-cols-2 gap-4">
                <Field label="Estado (UF)" hint="Centro padrão do mapa" error={errors.uf?.message}>
                  <select
                    {...register('uf')}
                    className="w-full rounded-xl bg-midnight-2/60 border border-hairline px-3 py-2.5 text-sm text-ink outline-none focus:border-accent/40 transition-all"
                  >
                    <option value="">— Selecione —</option>
                    {['AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT',
                      'PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO'
                    ].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Município" hint="Geocodificado no mapa" error={errors.municipio?.message}>
                  <input
                    {...register('municipio')}
                    type="text"
                    className="w-full rounded-xl bg-midnight-2/60 border border-hairline px-3 py-2.5 text-sm text-ink placeholder-ink-faint outline-none focus:border-accent/40 transition-all"
                    placeholder="Ex.: Maceió, São Paulo…"
                  />
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Cor primária (#RRGGBB)" error={errors.primary_color?.message}>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={primaryPreview}
                      onChange={(e) => setValue('primary_color', e.target.value, { shouldDirty: true })}
                      className="h-9 w-12 rounded border border-hairline bg-midnight-2 cursor-pointer"
                    />
                    <input
                      {...register('primary_color')}
                      type="text"
                      className="w-full rounded-xl bg-midnight-2/60 border border-hairline px-3 py-2.5 text-sm text-ink placeholder-ink-faint outline-none focus:border-accent/40 transition-all flex-grow font-mono"
                    />
                  </div>
                </Field>
                <Field label="Cor de destaque (#RRGGBB)" error={errors.accent_color?.message}>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={accentPreview}
                      onChange={(e) => setValue('accent_color', e.target.value, { shouldDirty: true })}
                      className="h-9 w-12 rounded border border-hairline bg-midnight-2 cursor-pointer"
                    />
                    <input
                      {...register('accent_color')}
                      type="text"
                      className="w-full rounded-xl bg-midnight-2/60 border border-hairline px-3 py-2.5 text-sm text-ink placeholder-ink-faint outline-none focus:border-accent/40 transition-all flex-grow font-mono"
                    />
                  </div>
                </Field>
              </div>

              <Field label="URL do logo" hint="HTTPS — opcional" error={errors.logo_url?.message}>
                <input {...register('logo_url')} type="url" className="w-full rounded-xl bg-midnight-2/60 border border-hairline px-3 py-2.5 text-sm text-ink placeholder-ink-faint outline-none focus:border-accent/40 transition-all" placeholder="https://…" />
              </Field>
            </div>
          ) : tab === 'membros' && tenantId ? (
            <MembersTab tenantId={tenantId} />
          ) : tab === 'whatsapp' && tenantId ? (
            <WhatsAppTab tenantId={tenantId} />
          ) : null}
        </form>

        {/* Footer */}
        <div className="px-6 py-4 hairline-t flex items-center justify-end gap-2 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl btn-ghost px-4 py-2 text-sm font-medium cursor-pointer"
          >
            {tab === 'membros' || tab === 'whatsapp' ? 'Fechar' : 'Cancelar'}
          </button>
          {tab !== 'membros' && tab !== 'whatsapp' && (
            <button
              type="button"
              onClick={onSubmit}
              disabled={submitting || (isEdit && !isDirty)}
              className="flex items-center gap-2 rounded-xl btn-accent px-4 py-2 text-sm font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-default"
            >
              {submitting
                ? <><Loader2 className="h-4 w-4 animate-spin" /> Salvando…</>
                : <><Save className="h-4 w-4" /> {isEdit ? 'Salvar alterações' : 'Criar tenant'}</>
              }
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── Helpers UI ───────────────────────────────────────────────────────────────

function TabBtn({
  icon: Icon, label, active, onClick,
}: { icon: typeof Building2; label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer ${
        active
          ? 'bg-accent/15 text-accent border border-accent/25'
          : 'text-ink-muted hover:text-ink hover:bg-white/5'
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}

function Field({
  label, hint, error, children,
}: { label: string; hint?: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-semibold text-ink-muted">{label}</span>
        {hint && <span className="text-[10px] text-ink-subtle">{hint}</span>}
      </label>
      {children}
      {error && (
        <p className="mt-1 text-[11px] text-danger flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          {error}
        </p>
      )}
    </div>
  );
}

// ── Aba WhatsApp ──────────────────────────────────────────────────────────────

function WhatsAppTab({ tenantId }: { tenantId: string }) {
  const { data: wa, isLoading }           = useTenantWhatsApp(tenantId);
  const { data: planLimits }              = useTenantPlanLimits(tenantId);
  const { data: msgSettings }             = useTenantMessagingSettings(tenantId);
  const upsert                            = useUpsertTenantWhatsApp();
  const updateStatus                      = useUpdateWhatsAppStatus();
  const updatePlanLimits                  = useUpdateTenantPlanLimits();
  const upsertMsgSettings                 = useUpsertMessagingSettings();
  const deleteMutation                    = useDeleteTenantWhatsApp();

  const [displayPhone,      setDisplayPhone]      = useState('');
  const [phoneNumberId,     setPhoneNumberId]     = useState('');
  const [wabaId,            setWabaId]            = useState('');
  const [tier,              setTier]              = useState(1);
  const [dailyLimit,        setDailyLimit]        = useState(250);
  const [cooldownMinutes,   setCooldownMinutes]   = useState(1440);
  const [sendWindowStart,   setSendWindowStart]   = useState('09:00');
  const [sendWindowEnd,     setSendWindowEnd]     = useState('20:00');
  const [lookupLoading,     setLookupLoading]     = useState(false);
  const [lookupResult,      setLookupResult]      = useState<{ verifiedName: string; status: string } | null>(null);

  // Sincroniza form quando dado carrega
  useEffect(() => {
    if (wa) {
      setDisplayPhone(wa.display_phone);
      setPhoneNumberId(wa.phone_number_id ?? '');
      setWabaId(wa.waba_id ?? '');
      setTier(wa.tier);
      setDailyLimit(wa.daily_limit);
    }
  }, [wa]);

  useEffect(() => {
    if (planLimits) {
      setCooldownMinutes(planLimits.min_cooldown_minutes);
    }
  }, [planLimits]);

  useEffect(() => {
    if (msgSettings) {
      setSendWindowStart(msgSettings.send_window_start);
      setSendWindowEnd(msgSettings.send_window_end);
    }
  }, [msgSettings]);

  const handleLookup = async () => {
    const phone = displayPhone.trim();
    if (!phone) {
      toast.error('Informe o número E.164 antes de buscar');
      return;
    }
    setLookupLoading(true);
    setLookupResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('lookup-whatsapp-number', {
        body: { phone },
      });
      if (error) throw error;
      const result = data as {
        id?: string; wabaId?: string; tier?: number;
        verifiedName?: string; status?: string; error?: string;
        available?: string[];
      };
      if (result.error === 'NOT_FOUND') {
        toast.error('Número não encontrado na conta YCloud', {
          description: result.available?.length
            ? `Números disponíveis: ${result.available.join(', ')}`
            : 'Verifique se o número está registrado na conta master.',
        });
        return;
      }
      if (result.error) {
        toast.error('Erro ao consultar YCloud', { description: result.error });
        return;
      }
      // Preenche os campos automaticamente
      if (result.id)     setPhoneNumberId(result.id);
      if (result.wabaId) setWabaId(result.wabaId);
      if (result.tier)   setTier(result.tier);
      setLookupResult({ verifiedName: result.verifiedName ?? '', status: result.status ?? '' });
      toast.success('IDs preenchidos automaticamente', {
        description: `${result.verifiedName ?? phone} — ${result.status}`,
      });
    } catch (e) {
      toast.error('Falha na busca', { description: e instanceof Error ? e.message : String(e) });
    } finally {
      setLookupLoading(false);
    }
  };

  const handleSave = async () => {
    if (!displayPhone.trim()) {
      toast.error('Número de telefone obrigatório (E.164, ex: +5582999990000)');
      return;
    }
    try {
      await Promise.all([
        upsert.mutateAsync({
          tenant_id:       tenantId,
          display_phone:   displayPhone,
          phone_number_id: phoneNumberId || null,
          waba_id:         wabaId || null,
          tier,
          daily_limit:     dailyLimit,
          // Ao salvar uma configuração válida, marcar como ativo automaticamente.
          // O admin pode suspender/desativar depois via botões de transição.
          status:          wa?.status === 'suspended' || wa?.status === 'inactive'
                             ? wa.status
                             : 'active',
        }),
        updatePlanLimits.mutateAsync({
          tenant_id:            tenantId,
          min_cooldown_minutes: cooldownMinutes,
        }),
        upsertMsgSettings.mutateAsync({
          tenant_id:         tenantId,
          send_window_start: sendWindowStart,
          send_window_end:   sendWindowEnd,
        }),
      ]);
      toast.success('Configuração WhatsApp salva');
    } catch (e) {
      toast.error('Falha ao salvar', { description: e instanceof Error ? e.message : String(e) });
    }
  };

  const handleStatusChange = (status: WhatsAppStatus) => {
    updateStatus.mutate(
      { tenant_id: tenantId, status },
      {
        onSuccess: () => toast.success(`Status alterado para: ${WA_STATUS_LABELS[status]}`),
        onError:   (e) => toast.error('Falha ao alterar status', { description: String(e) }),
      },
    );
  };

  const handleDelete = () => {
    if (!confirm('Remover configuração WhatsApp deste tenant? Os disparos serão bloqueados imediatamente.')) return;
    deleteMutation.mutate(tenantId, {
      onSuccess: () => { toast.success('Configuração WhatsApp removida'); setDisplayPhone(''); },
      onError:   (e) => toast.error('Falha ao remover', { description: String(e) }),
    });
  };

  const inputCls = 'w-full rounded-xl bg-midnight-2/60 border border-hairline px-3 py-2.5 text-sm text-ink placeholder-ink-faint outline-none focus:border-accent/40 transition-all';

  if (isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-5 w-5 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Info do modelo de conta */}
      <div className="rounded-xl bg-accent/5 border border-accent/20 p-3 flex gap-3 items-start">
        <ShieldCheck className="h-4 w-4 text-accent shrink-0 mt-0.5" />
        <p className="text-xs text-ink-muted leading-relaxed">
          <strong className="text-ink">Conta master YCloud</strong> — todos os tenants compartilham
          a mesma conta com WABAs independentes. A API key fica no Secret
          <code className="mx-1 bg-midnight-3 px-1.5 py-0.5 rounded text-[10px] font-mono text-accent">YCLOUD_API_KEY</code>
          da plataforma. Cada número aqui é único na plataforma.
        </p>
      </div>

      {/* Status atual */}
      {wa && (
        <div className="flex items-center justify-between p-3 rounded-xl border border-hairline bg-midnight-2/30">
          <div className="flex items-center gap-2">
            {wa.status === 'active'
              ? <Wifi className="h-4 w-4 text-emerald-400" />
              : <WifiOff className="h-4 w-4 text-ink-subtle" />
            }
            <div>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${WA_STATUS_COLORS[wa.status]}`}>
                {WA_STATUS_LABELS[wa.status]}
              </span>
              {wa.connected_at && (
                <p className="text-[10px] text-ink-subtle mt-0.5">
                  Conectado em {new Date(wa.connected_at).toLocaleDateString('pt-BR')}
                </p>
              )}
            </div>
          </div>
          {/* Transições de status */}
          <div className="flex gap-1">
            {wa.status !== 'active' && (
              <button type="button" onClick={() => handleStatusChange('active')}
                className="text-[10px] font-semibold px-3 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25 cursor-pointer transition-colors">
                Ativar
              </button>
            )}
            {wa.status === 'active' && (
              <button type="button" onClick={() => handleStatusChange('suspended')}
                className="text-[10px] font-semibold px-3 py-1.5 rounded-lg bg-danger/10 text-danger border border-danger/30 hover:bg-danger/20 cursor-pointer transition-colors">
                Suspender
              </button>
            )}
            {wa.status !== 'pending' && wa.status !== 'inactive' && (
              <button type="button" onClick={() => handleStatusChange('inactive')}
                className="text-[10px] font-semibold px-3 py-1.5 rounded-lg bg-midnight-3 text-ink-muted border border-hairline hover:bg-midnight-4 cursor-pointer transition-colors">
                Inativar
              </button>
            )}
          </div>
        </div>
      )}

      {/* Formulário */}
      <div className="space-y-4">
        <Field
          label="Número de exibição (E.164)"
          hint="Único na plataforma — campo 'from' do payload YCloud"
          error={displayPhone && !displayPhone.startsWith('+') ? 'Use formato E.164: +5582...' : undefined}
        >
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-ink-subtle shrink-0" />
            <input
              type="tel"
              value={displayPhone}
              onChange={(e) => { setDisplayPhone(e.target.value); setLookupResult(null); }}
              placeholder="+5582999990000"
              className={inputCls}
            />
            <button
              type="button"
              onClick={() => void handleLookup()}
              disabled={lookupLoading || !displayPhone.trim()}
              title="Buscar Phone Number ID e WABA ID automaticamente via API YCloud"
              className="flex items-center gap-1.5 shrink-0 rounded-xl px-3 py-2.5 text-xs font-semibold border border-accent/30 text-accent bg-accent/10 hover:bg-accent/20 disabled:opacity-40 disabled:cursor-default transition-colors cursor-pointer"
            >
              {lookupLoading
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Search className="h-3.5 w-3.5" />
              }
              Buscar IDs
            </button>
          </div>
          {/* Feedback de lookup bem-sucedido */}
          {lookupResult && (
            <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-emerald-400">
              <CheckCircle className="h-3 w-3" />
              <span>{lookupResult.verifiedName} · {lookupResult.status}</span>
            </div>
          )}
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Phone Number ID" hint="Do dashboard YCloud">
            <input
              type="text"
              value={phoneNumberId}
              onChange={(e) => setPhoneNumberId(e.target.value)}
              placeholder="Ex: 123456789012345"
              className={inputCls}
            />
          </Field>
          <Field label="WABA ID" hint="WhatsApp Business Account ID">
            <input
              type="text"
              value={wabaId}
              onChange={(e) => setWabaId(e.target.value)}
              placeholder="Ex: 987654321098765"
              className={inputCls}
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Tier Meta" hint="Controla limite diário de conversações">
            <select
              value={tier}
              onChange={(e) => setTier(Number(e.target.value))}
              className={inputCls}
            >
              {Object.entries(META_TIER_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </Field>
          <Field label="Limite diário (override)" hint="0 = usa o tier como referência">
            <input
              type="number"
              value={dailyLimit}
              onChange={(e) => setDailyLimit(Number(e.target.value))}
              min={0}
              max={100000}
              className={inputCls}
            />
          </Field>
        </div>

        {/* Janela de envio */}
        <div className="rounded-xl border border-hairline bg-midnight-2/20 p-4 space-y-3">
          <p className="text-xs font-bold text-ink flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5 text-accent" />
            Janela de envio
            <span className="text-[10px] font-normal text-ink-subtle ml-1">
              — horário local do tenant (fuso configurado na aba Geral)
            </span>
          </p>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Início" hint="Primeiro horário permitido">
              <input
                type="time"
                value={sendWindowStart}
                onChange={(e) => setSendWindowStart(e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field label="Fim" hint="Último horário permitido">
              <input
                type="time"
                value={sendWindowEnd}
                onChange={(e) => setSendWindowEnd(e.target.value)}
                className={inputCls}
              />
            </Field>
          </div>
          <p className="text-[10px] text-ink-subtle">
            Campanhas disparadas fora desta janela serão bloqueadas até o próximo período permitido.
            Padrão: 09:00 – 20:00.
          </p>
        </div>

        {/* Cooldown entre disparos */}
        <Field
          label="Cooldown entre campanhas (minutos)"
          hint={
            cooldownMinutes === 0
              ? 'Sem cooldown — mesmo contato pode receber campanhas consecutivas'
              : cooldownMinutes < 60
                ? `${cooldownMinutes} min entre campanhas para o mesmo contato`
                : cooldownMinutes < 1440
                  ? `${Math.round(cooldownMinutes / 60)}h entre campanhas para o mesmo contato`
                  : `${Math.round(cooldownMinutes / 1440)} dia(s) entre campanhas para o mesmo contato`
          }
        >
          <div className="flex items-center gap-3">
            <input
              type="number"
              value={cooldownMinutes}
              onChange={(e) => setCooldownMinutes(Math.max(0, Math.min(10080, Number(e.target.value))))}
              min={0}
              max={10080}
              step={60}
              className={inputCls}
            />
            <div className="flex gap-1 shrink-0">
              {[0, 60, 360, 720, 1440].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setCooldownMinutes(v)}
                  className={`px-2 py-1 rounded-lg text-[10px] font-semibold border transition-colors cursor-pointer ${
                    cooldownMinutes === v
                      ? 'bg-accent/20 text-accent border-accent/40'
                      : 'bg-midnight-2 text-ink-muted border-hairline hover:border-accent/30'
                  }`}
                >
                  {v === 0 ? 'Sem' : v < 60 ? `${v}m` : v < 1440 ? `${v / 60}h` : `${v / 1440}d`}
                </button>
              ))}
            </div>
          </div>
        </Field>
      </div>

      {/* Templates cadastrados */}
      <TemplatesSection tenantId={tenantId} inputCls={inputCls} />

      {/* Ações */}
      <div className="flex items-center justify-between pt-2 hairline-t">
        {wa ? (
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="flex items-center gap-1.5 text-xs text-danger hover:underline cursor-pointer disabled:opacity-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Remover configuração
          </button>
        ) : (
          <span className="text-xs text-ink-subtle italic">Nenhuma configuração ainda.</span>
        )}

        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={upsert.isPending}
          className="flex items-center gap-2 rounded-xl btn-accent px-4 py-2 text-sm font-semibold cursor-pointer disabled:opacity-50"
        >
          {upsert.isPending
            ? <><Loader2 className="h-4 w-4 animate-spin" /> Salvando…</>
            : <><Save className="h-4 w-4" /> Salvar WhatsApp</>
          }
        </button>
      </div>
    </div>
  );
}

// ── Templates Section ─────────────────────────────────────────────────────────

const HEADER_TYPE_LABELS: Record<HeaderType, string> = {
  NONE:     'Sem header',
  TEXT:     'Texto',
  IMAGE:    'Imagem',
  VIDEO:    'Vídeo',
  DOCUMENT: 'Documento',
};

const USE_CASE_LABELS: Record<TemplateUseCase, string> = {
  checkin:           'Check-in',
  event_confirm:     'Confirmação de evento',
  material_received: 'Recebimento de material',
  prayer:            'Oração / intercessão',
  leadership:        'Liderança',
  general:           'Geral',
};

const STATUS_COLORS: Record<string, string> = {
  approved: 'text-emerald-300 bg-emerald-500/15 border-emerald-500/30',
  pending:  'text-amber-300 bg-amber-500/15 border-amber-500/30',
  rejected: 'text-danger bg-danger/15 border-danger/30',
  paused:   'text-ink-subtle bg-midnight-3/50 border-hairline',
};

function TemplatesSection({ tenantId, inputCls }: { tenantId: string; inputCls: string }) {
  const { data: templates = [], isLoading, refetch } = useWhatsAppTemplates(tenantId);
  const updateTemplate = useUpdateWhatsAppTemplate();
  const [expanded,  setExpanded]  = useState<string | null>(null);
  const [edits,     setEdits]     = useState<Record<string, Partial<WhatsAppTemplate>>>({});
  const [syncing,   setSyncing]   = useState(false);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke('sync-whatsapp-templates', {
        body: { tenantId },
      });
      if (error) throw error;
      await refetch();
      const result = data?.data as { synced?: number; created?: number; updated?: number } | undefined;
      toast.success('Templates sincronizados', {
        description: result
          ? `${result.synced ?? 0} processado(s) — ${result.created ?? 0} novo(s), ${result.updated ?? 0} atualizado(s)`
          : 'Sincronização concluída',
      });
    } catch (e) {
      toast.error('Falha ao sincronizar', { description: e instanceof Error ? e.message : String(e) });
    } finally {
      setSyncing(false);
    }
  };

  const getEdit = (id: string): Partial<WhatsAppTemplate> => edits[id] ?? {};
  const setEdit = (id: string, patch: Partial<WhatsAppTemplate>) =>
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const handleSave = async (tpl: WhatsAppTemplate) => {
    const edit = getEdit(tpl.id);
    try {
      await updateTemplate.mutateAsync({
        id:               tpl.id,
        tenant_id:        tpl.tenant_id,
        use_case:         edit.use_case        ?? tpl.use_case,
        header_type:      edit.header_type     ?? tpl.header_type,
        header_text:      edit.header_text     ?? tpl.header_text,
        header_media_url: edit.header_media_url ?? tpl.header_media_url,
      });
      toast.success(`Template "${tpl.name}" atualizado`);
      setExpanded(null);
    } catch (e) {
      toast.error('Falha ao salvar template', { description: e instanceof Error ? e.message : String(e) });
    }
  };

  if (isLoading) return (
    <div className="flex justify-center py-4">
      <Loader2 className="h-4 w-4 animate-spin text-accent" />
    </div>
  );

  return (
    <div className="rounded-xl border border-hairline bg-midnight-2/20 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-ink flex items-center gap-1.5">
          <MessageCircle className="h-3.5 w-3.5 text-accent" />
          Templates cadastrados
          <span className="text-[10px] font-normal text-ink-subtle ml-1">
            — sincronize para atualizar status do YCloud
          </span>
        </p>
        <button
          type="button"
          onClick={() => void handleSync()}
          disabled={syncing}
          className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold border border-accent/30 text-accent bg-accent/10 hover:bg-accent/20 disabled:opacity-40 disabled:cursor-default transition-colors cursor-pointer"
        >
          {syncing
            ? <Loader2 className="h-3 w-3 animate-spin" />
            : <RefreshCw className="h-3 w-3" />
          }
          {syncing ? 'Sincronizando…' : 'Sincronizar'}
        </button>
      </div>

      {templates.length === 0 ? (
        <p className="text-xs text-ink-subtle italic text-center py-2">
          Nenhum template ainda. Execute "Sincronizar Templates" após aprovar no YCloud.
        </p>
      ) : (
        <div className="space-y-2">
          {templates.map((tpl) => {
            const edit       = getEdit(tpl.id);
            const isOpen     = expanded === tpl.id;
            const headerType = (edit.header_type ?? tpl.header_type) as HeaderType;
            const needsUrl   = ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerType);

            return (
              <div key={tpl.id} className="rounded-xl border border-hairline overflow-hidden">
                {/* Row */}
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : tpl.id)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-white/3 transition-colors cursor-pointer"
                >
                  <div className="flex-grow min-w-0">
                    <p className="text-xs font-semibold text-ink truncate font-mono">{tpl.name}</p>
                    <p className="text-[10px] text-ink-subtle">{tpl.language} · {USE_CASE_LABELS[tpl.use_case]}</p>
                  </div>
                  <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${STATUS_COLORS[tpl.status] ?? STATUS_COLORS.pending}`}>
                    {tpl.status === 'approved' ? '✓ Aprovado' : tpl.status === 'rejected' ? '✗ Rejeitado' : tpl.status === 'paused' ? '⏸ Pausado' : '⏳ Pendente'}
                  </span>
                  <span className="text-ink-subtle text-[10px] shrink-0">{isOpen ? '▲' : '▼'}</span>
                </button>

                {/* Expanded editor */}
                {isOpen && (
                  <div className="border-t border-hairline px-3 py-3 space-y-3 bg-midnight-2/30">
                    {/* Prévia do body */}
                    <div className="rounded-lg bg-midnight-3/50 px-3 py-2">
                      <p className="text-[10px] text-ink-subtle mb-1">Corpo do template</p>
                      <p className="text-xs text-ink-muted whitespace-pre-wrap leading-relaxed">{tpl.body}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      {/* Use case */}
                      <Field label="Contexto de uso">
                        <select
                          value={edit.use_case ?? tpl.use_case}
                          onChange={(e) => setEdit(tpl.id, { use_case: e.target.value as TemplateUseCase })}
                          className={inputCls}
                        >
                          {(Object.entries(USE_CASE_LABELS) as [TemplateUseCase, string][]).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                          ))}
                        </select>
                      </Field>

                      {/* Header type */}
                      <Field label="Tipo do header">
                        <select
                          value={headerType}
                          onChange={(e) => setEdit(tpl.id, { header_type: e.target.value as HeaderType, header_media_url: '' })}
                          className={inputCls}
                        >
                          {(Object.entries(HEADER_TYPE_LABELS) as [HeaderType, string][]).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                          ))}
                        </select>
                      </Field>
                    </div>

                    {/* Header texto fixo */}
                    {headerType === 'TEXT' && (
                      <Field label="Texto do header" hint="Fixo, max 60 chars">
                        <input
                          type="text"
                          maxLength={60}
                          value={edit.header_text ?? tpl.header_text ?? ''}
                          onChange={(e) => setEdit(tpl.id, { header_text: e.target.value })}
                          className={inputCls}
                          placeholder="Ex.: Comunicado da equipe"
                        />
                      </Field>
                    )}

                    {/* URL de mídia estática */}
                    {needsUrl && (
                      <Field
                        label={`URL ${HEADER_TYPE_LABELS[headerType]} (estática)`}
                        hint="HTTPS público — pode ser sobrescrita por URL dinâmica no disparo"
                      >
                        <input
                          type="url"
                          value={edit.header_media_url ?? tpl.header_media_url ?? ''}
                          onChange={(e) => setEdit(tpl.id, { header_media_url: e.target.value })}
                          className={inputCls}
                          placeholder="https://…"
                        />
                      </Field>
                    )}

                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => void handleSave(tpl)}
                        disabled={updateTemplate.isPending}
                        className="flex items-center gap-1.5 rounded-xl btn-accent px-3 py-1.5 text-xs font-semibold cursor-pointer disabled:opacity-50"
                      >
                        {updateTemplate.isPending
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <Save className="h-3 w-3" />
                        }
                        Salvar template
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function nullify(v: string | null | undefined): string | null {
  if (v === undefined || v === null) return null;
  const trimmed = v.trim();
  return trimmed === '' ? null : trimmed;
}

// ── Aba de membros ────────────────────────────────────────────────────────────

function MembersTab({ tenantId }: { tenantId: string }) {
  const { data: members = [], isLoading: loadingMembers } = useTenantMembers(tenantId);
  const { data: invitations = [], isLoading: loadingInvites } = useTenantInvitations(tenantId);

  const updateRole   = useUpdateMemberRole();
  const toggleActive = useToggleMemberActive();
  const removeMember = useRemoveMember();
  const createInvite = useCreateInvitation();
  const revokeInvite = useRevokeInvitation();

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole]   = useState<TenantRole>('lider');

  // Usuário logado como invited_by
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setCurrentUserId(data.user?.id ?? null)).catch(() => {/* no-op */});
  }, []);

  const handleInvite = async () => {
    const email = inviteEmail.trim();
    if (!email) return;
    try {
      await createInvite.mutateAsync({ tenant_id: tenantId, email, role: inviteRole, invited_by: currentUserId });
      toast.success(`Convite enviado para ${email}`);
      setInviteEmail('');
    } catch (e) {
      toast.error('Falha ao convidar', { description: e instanceof Error ? e.message : String(e) });
    }
  };

  const isLoading = loadingMembers || loadingInvites;

  return (
    <div className="space-y-6">
      {isLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-accent" />
        </div>
      ) : (
        <>
          {/* ── Membros ativos ── */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold text-ink uppercase tracking-wider">
                Membros ({members.length})
              </h3>
            </div>
            {members.length === 0 ? (
              <p className="text-xs text-ink-subtle text-center py-4">Nenhum membro ainda.</p>
            ) : (
              <div className="space-y-2">
                {members.map((m) => {
                  const initial = (m.display_name || m.full_name)[0]?.toUpperCase() ?? '?';
                  return (
                    <div
                      key={m.user_id}
                      className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                        m.active
                          ? 'bg-midnight-2/40 border-hairline'
                          : 'bg-midnight-3/30 border-hairline opacity-50'
                      }`}
                    >
                      {/* Avatar */}
                      <div className="h-9 w-9 rounded-xl bg-accent/15 border border-accent/25 flex items-center justify-center text-sm font-bold text-accent shrink-0">
                        {m.avatar_url
                          ? <img src={m.avatar_url} alt="" className="h-9 w-9 rounded-xl object-cover" />
                          : initial
                        }
                      </div>

                      {/* Info */}
                      <div className="flex-grow min-w-0">
                        <p className="text-sm font-semibold text-ink truncate">
                          {m.display_name || m.full_name}
                        </p>
                        <p className="text-[10px] text-ink-subtle">
                          desde {new Date(m.joined_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </p>
                      </div>

                      {/* Role selector */}
                      <select
                        value={m.role}
                        onChange={(e) => {
                          updateRole.mutate(
                            { tenant_id: tenantId, user_id: m.user_id, role: e.target.value as TenantRole },
                            { onError: (err) => toast.error('Falha ao alterar papel', { description: String(err) }) },
                          );
                        }}
                        className={`text-[10px] font-semibold px-2 py-1 rounded-lg border cursor-pointer bg-transparent outline-none ${ROLE_COLORS[m.role]}`}
                      >
                        {(Object.entries(ROLE_LABELS) as [TenantRole, string][]).map(([k, v]) => (
                          <option key={k} value={k} className="bg-midnight-3 text-ink">{v}</option>
                        ))}
                      </select>

                      {/* Toggle ativo */}
                      <button
                        type="button"
                        title={m.active ? 'Suspender acesso' : 'Restaurar acesso'}
                        onClick={() => toggleActive.mutate(
                          { tenant_id: tenantId, user_id: m.user_id, active: !m.active },
                          { onError: (err) => toast.error('Falha ao alterar status', { description: String(err) }) },
                        )}
                        className="rounded-lg p-1.5 cursor-pointer transition-colors text-ink-subtle hover:text-amber-300 hover:bg-amber-500/10"
                      >
                        {m.active
                          ? <ToggleRight className="h-4 w-4 text-emerald-400" />
                          : <ToggleLeft  className="h-4 w-4 text-ink-subtle" />
                        }
                      </button>

                      {/* Remover */}
                      <button
                        type="button"
                        title="Remover do tenant"
                        onClick={() => {
                          if (!confirm(`Remover ${m.display_name || m.full_name} do tenant? O usuário perderá o acesso imediatamente.`)) return;
                          removeMember.mutate(
                            { tenant_id: tenantId, user_id: m.user_id },
                            {
                              onSuccess: () => toast.success('Membro removido'),
                              onError:   (err) => toast.error('Falha ao remover', { description: String(err) }),
                            },
                          );
                        }}
                        className="rounded-lg p-1.5 cursor-pointer transition-colors text-ink-subtle hover:text-danger hover:bg-danger/10"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* ── Convites pendentes ── */}
          <section>
            <h3 className="text-xs font-bold text-ink uppercase tracking-wider mb-3">
              Convites pendentes ({invitations.length})
            </h3>
            {invitations.length > 0 && (
              <div className="space-y-2 mb-4">
                {invitations.map((inv) => {
                  const expired = new Date(inv.expires_at) < new Date();
                  return (
                    <div
                      key={inv.id}
                      className={`flex items-center gap-3 p-3 rounded-xl border ${
                        expired ? 'border-danger/20 bg-danger/5 opacity-60' : 'border-hairline bg-midnight-2/30'
                      }`}
                    >
                      <Mail className="h-4 w-4 text-ink-subtle shrink-0" />
                      <div className="flex-grow min-w-0">
                        <p className="text-xs font-semibold text-ink truncate">{inv.email}</p>
                        <p className="text-[10px] text-ink-subtle flex items-center gap-1">
                          <Clock className="h-2.5 w-2.5" />
                          {expired
                            ? 'Expirado'
                            : `Expira ${new Date(inv.expires_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}`
                          }
                        </p>
                      </div>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${ROLE_COLORS[inv.role]}`}>
                        {ROLE_LABELS[inv.role]}
                      </span>
                      <button
                        type="button"
                        title="Revogar convite"
                        onClick={() => revokeInvite.mutate(
                          { id: inv.id, tenant_id: tenantId },
                          {
                            onSuccess: () => toast.success('Convite revogado'),
                            onError:   (err) => toast.error('Falha ao revogar', { description: String(err) }),
                          },
                        )}
                        className="rounded-lg p-1.5 cursor-pointer transition-colors text-ink-subtle hover:text-danger hover:bg-danger/10"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Formulário de convite */}
            <div className="rounded-xl border border-accent/20 bg-accent/5 p-4">
              <p className="text-xs font-semibold text-ink mb-3 flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5 text-accent" />
                Convidar novo membro
              </p>
              <div className="flex gap-2 flex-col sm:flex-row">
                <input
                  type="email"
                  placeholder="email@exemplo.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleInvite(); } }}
                  className="flex-grow rounded-xl bg-midnight-2/60 border border-hairline px-3 py-2 text-sm text-ink placeholder-ink-faint outline-none focus:border-accent/40 transition-all"
                />
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as TenantRole)}
                  className="rounded-xl bg-midnight-2/60 border border-hairline px-3 py-2 text-sm text-ink outline-none focus:border-accent/40 transition-all cursor-pointer"
                >
                  {(Object.entries(ROLE_LABELS) as [TenantRole, string][]).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handleInvite}
                  disabled={!inviteEmail.trim() || createInvite.isPending}
                  className="flex items-center gap-1.5 rounded-xl btn-accent px-4 py-2 text-sm font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-default shrink-0"
                >
                  {createInvite.isPending
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <CheckCircle2 className="h-3.5 w-3.5" />
                  }
                  Convidar
                </button>
              </div>
              <p className="mt-2 text-[10px] text-ink-subtle">
                O convite expira em 7 dias. O usuário receberá um link de acesso ao se cadastrar com esse e-mail.
              </p>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
